import React, { useState, useCallback, useRef, useEffect } from "react";
import useFetch from "../../../../../hooks/useFetch";

// Drives the whole participant game screen. Polls /session/:id (same
// lightweight polling pattern as Phase 1, not sockets — per agreed scope).
// The timer is always DERIVED from server timestamps, never a local
// setInterval countdown that's trusted on its own — that's what lets pause
// freeze it correctly without client/server drift accumulating.

const SESSION_POLL_MS = 4000;

export default function useReunionGame(eventId) {
  const { fetchData } = useFetch();

  const [session, setSession] = useState(null); // raw server response for /session
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { correct, advanced, gameComplete, newLevel }
  const [error, setError] = useState("");

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const fetchSessionOnce = useCallback(async () => {
    try {
      const response = await fetchData({
        url: `/reunion/game/session/${eventId}`,
        method: "GET",
      });

      if (response?.status) {
        setSession(response.data);
      }
    } catch (err) {
      // Silent on poll failures, same rationale as Phase 1's live-feed poll —
      // one dropped poll shouldn't blank out a mid-answer screen.
    }
  }, [eventId, fetchData]);

  useEffect(() => {
    fetchSessionOnce();
    pollRef.current = setInterval(fetchSessionOnce, SESSION_POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [eventId]);

  // Recompute remaining time every second from server timestamps — this is
  // what makes pause "just work" without a special client-side branch: while
  // eventIsPaused is true, the server simply stops advancing session state,
  // so questionStartedAt/totalPausedMs stay frozen and remaining stays put.
  useEffect(() => {
    if (!session || session.waiting || session.completed || !session.question) {
      setRemainingSeconds(null);
      return;
    }

    const compute = () => {
      const elapsedMs =
        Date.now() - new Date(session.questionStartedAt).getTime() - session.totalPausedMs;
      const remaining = session.question.time - Math.floor(elapsedMs / 1000);
      setRemainingSeconds(Math.max(0, remaining));
    };

    compute();
    tickRef.current = setInterval(compute, 1000);
    return () => clearInterval(tickRef.current);
  }, [session]);

  const startGame = useCallback(
    async (language) => {
      try {
        const response = await fetchData({
          url: `/reunion/game/start-game/${eventId}`,
          method: "POST",
          data: { language },
        });

        if (response?.status) {
          await fetchSessionOnce();
        }

        return response;
      } catch (err) {
        setError("Unable to start the game.");
        return null;
      }
    },
    [eventId, fetchData, fetchSessionOnce]
  );

  const submitAnswer = useCallback(
    async (answerPayload) => {
      setSubmitting(true);
      setError("");

      try {
        const response = await fetchData({
          url: `/reunion/game/answer/${eventId}`,
          method: "POST",
          data: answerPayload,
        });

        if (response?.status) {
          setLastResult(response.data);
          await fetchSessionOnce(); // pull the next question immediately, don't wait for the next poll tick
        } else {
          setError(response?.message || "Could not submit answer.");
        }

        return response;
      } catch (err) {
        setError("Unable to reach the server.");
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [eventId, fetchData, fetchSessionOnce]
  );

  return {
    session,
    remainingSeconds,
    submitting,
    lastResult,
    error,
    startGame,
    submitAnswer,
  };
}
